import {
  ButtonIcon,
  Card,
  CardButtonActionMode,
  HeaderGradient,
  MiniCard,
  Row,
} from '@app/components';
import React, { memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { ScrollView, StyleSheet, View } from 'react-native';
import { ICatalog } from '@app/common';
import SvgImage from '@app/components/icons/SvgImage';
import { UseFormReturn, useWatch } from 'react-hook-form';
import SvgInfo from '@app/components/icons/SvgInfo';
import { SlantTitle } from '@app/components/typography';
import { SvgArrowRight } from '@app/components/icons/SvgArrowRight';
import { SvgTransfer } from '@app/components/icons/SvgTransfer';

type CatalogCardProps = {
  catalog?: Partial<ICatalog>;
  formMethods: UseFormReturn<any, any, any>;
  onOpenImagePicker: () => void;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialCatalog: Partial<ICatalog>) => {
  return {
    name: partialCatalog?.name || 'Nouveau catalogue',
    ticketInfos: partialCatalog?.ticketInfos,
  };
};

export const CatalogCard = memo(
  ({ toggle, catalog, onOpenImagePicker, formMethods }: CatalogCardProps) => {
    const { subContainerHeight } = useDynamicSizes();

    const cardData = useWatch({
      control: formMethods.control,
    });

    const { name, ticketInfos } = formatCardData(cardData);

    const logo = catalog?.ticketInfos?.logo ?? ticketInfos?.logo;

    const previewUrl = cardData.image ? URL.createObjectURL(cardData.image) : null;

    return (
      <Card>
        <HeaderGradient>
          <LinearGradient
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            locations={[0, 1]}
            colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
            style={styles.headerGradient}
          />
        </HeaderGradient>

        <Card.TopHeader>
          <Card.TopHeaderLabel>Fiche catalogue</Card.TopHeaderLabel>
        </Card.TopHeader>

        <Card.Container height={subContainerHeight}>
          <Card.Background source={require('@app/assets/img/cardbackground.png')} />

          <Card.Content>
            <Card.Header>
              <Row justify="space-between" align="center">
                <Card.HeaderTitles>
                  <Card.Title numberOfLines={2}>{name}</Card.Title>
                  {catalog?._id && <Card.Subtitle>Ref: {catalog._id}</Card.Subtitle>}
                </Card.HeaderTitles>

                <Card.ButtonAction onPress={toggle} permissionScope="catalogs" />
              </Row>
            </Card.Header>

            <Card.VisualContainer>
              <Card.Visual
                // @ts-ignore
                // extraStyle={{ filter: 'grayscale(100%)' }}
                source={{ uri: previewUrl ? previewUrl : logo }}
              />

              <Card.VisualEdit>
                <ButtonIcon Icon={SvgImage} onPress={() => onOpenImagePicker()} />
              </Card.VisualEdit>
            </Card.VisualContainer>
          </Card.Content>

          <MiniCard>
            <MiniCard.Tabs>
              <MiniCard.TabsHolder>
                <MiniCard.Tab Icon={SvgInfo} isActive />
              </MiniCard.TabsHolder>
            </MiniCard.Tabs>
            <MiniCard.Container>
              <MiniCard.Content>
                <SlantTitle label="Aide" color={theme.colors.gold} />

                <Card.HelpScrollContainer extraStyle={{ paddingBottom: 24, paddingTop: 8 }}>
                  <Card.LineBreak height={16} />

                  <Card.HelpTitle>Fonctionnement des catalogues</Card.HelpTitle>

                  <Card.LineBreak height={8} />

                  <Card.HelpText>
                    Un catalogue sur Tick'eat représente une carte regroupant vos produits, menus et
                    promotions.
                  </Card.HelpText>

                  <Card.HelpText>
                    Il vous permet de structurer et d'organiser l'ensemble de votre offre
                    commerciale.
                  </Card.HelpText>

                  <Card.HelpText>
                    Une fois créé, le catalogue peut être publié{' '}
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <SvgTransfer width={16} height={16} color={theme.colors.celadonblue} />
                    </View>{' '}
                    sur un ou plusieurs connecteurs ( web, caisse, plateformes de livraison ... ),
                    afin de rendre vos produits accessibles à vos clients sur différents canaux.
                  </Card.HelpText>

                  <Card.HelpSubtitle>Exemples :</Card.HelpSubtitle>

                  <Card.HelpExample>Catalogue WEB</Card.HelpExample>
                  <Card.HelpExample>Catalogue Uber Eats / Deliveroo</Card.HelpExample>

                  <Card.LineBreak height={16} />
                </Card.HelpScrollContainer>
              </MiniCard.Content>
            </MiniCard.Container>
          </MiniCard>
        </Card.Container>
      </Card>
    );
  },
);

const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});
